import { Grid, Title, Text, Tooltip, Textarea, Container, ActionIcon, Flex } from '@mantine/core';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Note } from 'components/Note/Note';
import { Note as NoteModel } from 'models/general';
import { PortalButton } from 'components/Button/PortalButton';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { addComment, deleteComment, updateComment, useComments } from 'services/comments';
import { queryKeys } from 'utils/query';
import { BsPlus } from 'react-icons/bs';
import { DEFAULT_ICON_SIZE } from 'utils/general';

interface MeasurementDetailsIsCommentingProps {
  measurementId?: string;
  setIsCommenting: Dispatch<SetStateAction<boolean>>;
}

export const MeasurementDetailsIsCommenting = ({
  measurementId,
  setIsCommenting,
}: MeasurementDetailsIsCommentingProps) => {
  const { t } = useTranslation();
  const { data: commentList } = useComments(measurementId);
  const [commentText, setCommentText] = useState<string>('');
  const invalidateQueryKeys = [[queryKeys.measurementComments, measurementId]];
  const { mutate: add } = useCustomMutation({
    fn: addComment,
    onSuccess: { invalidateQueryKeys, callback: () => setCommentText('') },
  });
  const { mutate: update } = useCustomMutation({
    fn: updateComment,
    onSuccess: { invalidateQueryKeys },
  });
  const { mutate: remove } = useCustomMutation({
    fn: deleteComment,
    onSuccess: { invalidateQueryKeys },
  });

  const handleDeleteComment = (commentId: string) => {
    remove({ measurementId, commentId });
  };

  const handleUpdateComment = (commentId: string, updatedText: string) => {
    update({ commentId, measurementId, text: updatedText });
  };

  return (
    <Container>
      <Title my={'xl'} order={3}>
        {t('comments_label')}
      </Title>
      <Grid align={'center'}>
        <Grid.Col span={11}>
          <Textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            my={'lg'}
            autosize
            minRows={2}
            placeholder={t('patients_add_comment')}
            maxLength={250}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <Tooltip label={`${t('patients_add_comment')}`}>
            <ActionIcon
              variant={'filled'}
              size={'lg'}
              color={'var(--mantine-color-blue-5)'}
              disabled={!commentText}
              onClick={() => add({ measurementId, text: commentText })}
            >
              <BsPlus size={DEFAULT_ICON_SIZE} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={12}>
          {commentList && commentList?.length > 0 ? (
            commentList?.map((comment: NoteModel) => (
              <Note
                key={comment.id}
                id={comment.id || ''}
                text={comment.text || ''}
                updateTimestamp={comment.updateTimestamp || 0}
                author={comment.creatorId || ''}
                editTooltipLabel={t('patients_edit_comment')}
                deleteTooltipLabel={t('patients_delete_comment')}
                handleDeleteLogging={handleDeleteComment}
                handleUpdateLogging={handleUpdateComment}
              />
            ))
          ) : (
            <Text>{t('patients_no_comments')}</Text>
          )}
        </Grid.Col>
      </Grid>
      <Flex mt={'md'} justify={'flex-start'}>
        <PortalButton variant={'outline'} onClick={() => setIsCommenting(false)} label={t('go_back_label')} />
      </Flex>
    </Container>
  );
};
